import React, { useEffect, useRef, useState } from "react";
import {
  ModalCrosSvg,
  LocationSvg,
  UploadImgSvg,
  ImageCroseSvg,
  UploadPdfSvg,
} from "../../../images/Svgs";
import { AddEvent } from "../../../API/Events";
import { toast } from "react-toastify";
import LocationSearch from "../Location";
import { currentDate, CustomToolTip, useDragDrop } from "../../../utils/common";
import { Base_Url } from "../../../API/config";
import PdfViewer from "../EventDetail/PdfViewer";

const initionalEvent = {
  event_id: "",
  event_date: "",
  event_end_date: "",
  event_img: "",
  event_files: "",
  event_location: "",
  event_time_from: "",
  event_time_to: "",
  event_title: "",
  city: "",
  state: "",
  county: "",
  countries: "",
  latitude: "",
  logitude: "",
  zip_code: "",
};

const AddNewEvent = ({ onSetModal, editEvent }) => {
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [eventData, setEventData] = useState(initionalEvent);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [imageData, setImageData] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fileName, setFileName] = useState({
    image: "",
    pdf: "",
  });

  const [eventError, setEventError] = useState({
    event_title: "",
    event_date: "",
    event_end_date: "",
    event_time_to: "",
    event_time_from: "",
    event_location: "",
    event_img: "",
  });

  useEffect(() => {
    if (Object.keys(editEvent).length > 0) {
      const { event_img, event_files } = editEvent;
      const imageName = event_img ? event_img.split("/").pop() : "";

      const pdfName =
        event_files !== "null" ? event_files.split("/").pop() : "";

      setFileName({ image: imageName, pdf: pdfName });
      setEventData((prevData) => ({
        ...prevData,
        ...editEvent,
        event_id: editEvent.id,
      }));

      // If there's a PDF file in edit mode, show the preview
      if (pdfName) {
        setShowPdfPreview(true);
      }
    }
  }, [editEvent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData({ ...eventData, [name]: value });
    setEventError({ ...eventError, [name]: "" });
    if (name === "event_date") {
      setEventData({ ...eventData, event_date: value, event_end_date: "" });
    }
    if (name === "event_end_date") {
      setEventData({ ...eventData, event_end_date: value });
    }
  };

  // Validate form inputs
  const validateForm = () => {
    let errors = {};
    let isValid = true;

    // Title validation
    if (!eventData.event_title) {
      errors.event_title = "Title is required.";
      isValid = false;
    }

    // Start date validation
    if (!eventData.event_date) {
      errors.event_date = "Start date is required.";
      isValid = false;
    }
    // End date validation
    if (!eventData.event_end_date) {
      errors.event_end_date = "End date is required.";
      isValid = false;
    }
    // To time validation
    if (!eventData.event_time_to) {
      errors.event_time_to = "To time is required.";
      isValid = false;
    }
    // From time validation
    if (!eventData.event_time_from) {
      errors.event_time_from = "From time is required.";
      isValid = false;
    }

    // Status validation
    if (eventData.is_active === "") {
      errors.is_active = "Status is required.";
      isValid = false;
    }

    // Location validation
    if (eventData.event_location === "") {
      errors.event_location = "Location is required.";
      isValid = false;
    }
    // Image validation
    if (fileName?.image === "" || undefined) {
      errors.event_img = "Image is required.";
      isValid = false;
    }
    setEventError(errors);
    return isValid;
  };

  // Function to trigger file input click when the user clicks on the upload area
  const MAX_FILE_SIZE = 10.5 * 1024 * 1024; // 10 MB
  const allowedImageTypes = ["image/png", "image/jpeg"];
  const allowedFileTypes = ["application/pdf", "text/xml"];
  const handleFile = (file, inputName) => {
    if (file) {
      // Validate file type
      if (inputName === "event_img" && !allowedImageTypes.includes(file.type)) {
        setEventError({
          ...eventError,
          event_img: "Only PNG or JPG files are allowed.",
        });
        return;
      }
      if (
        inputName === "event_files" &&
        !allowedFileTypes.includes(file.type)
      ) {
        setEventError({
          ...eventError,
          event_files: "Only PDF or XML files are allowed.",
        });
        return;
      }
      // File size validation
      if (inputName === "event_files" && file?.size > MAX_FILE_SIZE) {
        setEventError({
          ...eventError,
          event_files: "File size must be less than or equal to 10 MB.",
        });
        toast.error('File size must be less than or equal to 10 MB.');
        return;
      }
      // Clear error and process the file
      setEventError({ ...eventError, [inputName]: "" });
      if (inputName === "event_img") {
        setFileName({ ...fileName, image: file.name });
        setSelectedImage(file);
      } else if (inputName === "event_files") {
        setFileName({ ...fileName, pdf: file.name });
        setSelectedPdf(file);
        setShowPdfPreview(true); // Show PDF preview when a file is selected
      }
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    // 🔒 Prevent multiple clicks
    if (isSubmitting) return;
    if (validateForm()) {
      try {
        setIsSubmitting(true); // lock button
        let formData = new FormData();
        formData.append("event_title", eventData?.event_title);
        formData.append("event_date", eventData?.event_date);
        formData.append("event_end_date", eventData?.event_end_date);
        formData.append("event_time_to", eventData?.event_time_to);
        formData.append("event_time_from", eventData?.event_time_from);
        formData.append("event_location", eventData?.event_location);
        formData.append("latitude", eventData?.latitude);
        formData.append("logitude", eventData?.logitude);
        formData.append("countries", eventData?.countries);
        formData.append("state", eventData?.state);
        formData.append("city", eventData?.city);
        formData.append("zip_code", eventData?.zip_code || null);
        if (Object.keys(editEvent).length > 0) {
          formData.append("event_id", editEvent?.id);
          formData.append(
            "event_img",
            selectedImage !== null ? selectedImage : editEvent?.event_img
          );
          formData.append(
            "event_files",
            selectedPdf !== null
              ? selectedPdf
              : fileName?.pdf !== ""
                ? editEvent?.event_files
                : ""
          );
        } else {
          formData.append("event_id", "");
          formData.append("event_img", selectedImage || null);
          formData.append("event_files", selectedPdf || null);
        }
        const response = await AddEvent(formData);
        if (response?.status) {
          toast.success(response.message || "success !");
          onSetModal(false);
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error("Error during login request:", error); // Handle any other errors
        toast.error(error?.message || error);
      }
    } else {
      console.log("Form has errors.");
    }
  };

  const { handleDragOver, handleDragLeave, handleDrop } = useDragDrop(
    handleFile,
    setDragActive,
    setDragActive,
    handleFile
  );

  return (
    <div className="modal fade show">
      <div className="modal-dialog add_new_event_box">
        <div className="modal-content">
          <div className="modal-header">
            <h1 className="modal-title fs-5 font-18" id="exampleModalLabel">{`${Object.keys(editEvent).length > 0
              ? "Update event"
              : "Add new event"
              }`}</h1>
            <button
              type="button"
              className="new_event_lose inter"
              onClick={() => onSetModal(false)}
            >
              <ModalCrosSvg />
            </button>
          </div>
          <div className="modal-body">
            <input
              type="text"
              className="form-control"
              placeholder="Event Title"
              name="event_title"
              maxLength={20}
              value={eventData?.event_title}
              onChange={(e) => handleChange(e)}
            />
            {eventError.event_title && (
              <div className="text-danger">{eventError.event_title}</div>
            )}
            <div className="Ddate_time mt-3">
              <div className=" icon_time">
                <h6 className="inter">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.1666 7.99992C15.1666 11.6799 12.18 14.6666 8.49998 14.6666C4.81998 14.6666 1.83331 11.6799 1.83331 7.99992C1.83331 4.31992 4.81998 1.33325 8.49998 1.33325C12.18 1.33325 15.1666 4.31992 15.1666 7.99992Z"
                      stroke="#7E7E7E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M10.9733 10.1199L8.90663 8.88659C8.54663 8.67326 8.2533 8.15992 8.2533 7.73992V5.00659"
                      stroke="#7E7E7E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>{" "}
                  &nbsp; Date & Time
                </h6>
              </div>
              <div className="event_date_box">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label inter">Event start date</label>
                    <input
                      type="date"
                      className="form-control mb-2"
                      id="eventStartDate"
                      title="Choose start date"
                      name="event_date"
                      value={eventData?.event_date}
                      onChange={(e) => handleChange(e)}
                      min={currentDate()}
                    />
                    {eventError.event_date && (
                      <div className="text-danger">{eventError.event_date}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label inter">Event end date</label>
                    <input
                      type="date"
                      className="form-control mb-2"
                      id="eventEndDate"
                      title="Choose end date"
                      name="event_end_date"
                      value={eventData?.event_end_date}
                      onChange={(e) => handleChange(e)}
                      min={eventData?.event_date || currentDate()}
                    />
                    {eventError.event_end_date && (
                      <div className="text-danger">
                        {eventError.event_end_date}
                      </div>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label
                      for="exampleFormControlInput1"
                      className="form-label mt-3 inter"
                    >
                      From
                    </label>
                    <input
                      type="time"
                      className="form-control mb-2"
                      name="event_time_from"
                      value={eventData?.event_time_from}
                      onChange={(e) => handleChange(e)}
                    />
                    {eventError.event_time_from && (
                      <div className="text-danger">
                        {eventError.event_time_from}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label
                      for="exampleFormControlInput1"
                      className="form-label mt-3 inter"
                    >
                      To
                    </label>
                    <input
                      type="time"
                      className="form-control mb-2"
                      name="event_time_to"
                      value={eventData?.event_time_to}
                      onChange={(e) => handleChange(e)}
                    />
                    {eventError.event_time_to && (
                      <div className="text-danger">
                        {eventError.event_time_to}
                      </div>
                    )}
                  </div>
                </div>
                <div className="location">
                  <LocationSearch
                    eventData={eventData}
                    setEventData={setEventData}
                    eventError={eventError}
                    setEventError={setEventError}
                    editEvent={editEvent}
                  />
                  <div className="location_icon">
                    <LocationSvg />
                  </div>
                  {eventError.event_location && (
                    <div className="text-danger">
                      {eventError.event_location}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div
                className={`event_file ${dragActive ? "drag-active" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  handleDrop(e);
                  handleFile(e.dataTransfer.files[0], "event_img");
                }}
              >
                <div className="upload-btn-wrapper">
                  <button
                    className="vitalu_cont  "
                    onClick={() => imageInputRef.current.click()}
                    disabled={selectedImage || fileName.image}
                  >
                    <div className="upload_icon">
                      <UploadImgSvg />
                    </div>

                    <div className="vital_text_png">
                      <span>Click to upload Event Image</span> or drag and drop
                    </div>
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    name="event_img"
                    onChange={(e) =>
                      handleFile(e.target.files[0], e.target.name)
                    }
                    style={{ display: "none" }}
                  />
                </div>
                {(selectedImage || fileName.image) && (
                  <>
                    <h2 className="inter">Uploaded PNG/JPG Files</h2>
                    <div className="sample_file_icon relative_postion">
                      <div className="img_container">
                        <img
                          src={
                            selectedImage
                              ? URL.createObjectURL(selectedImage)
                              : `${Base_Url}/${selectedImage ||
                              `uploads/admin/events/${fileName.image}`
                              }`
                          }
                          alt="Event Preview"
                          className="wh-full"
                          onLoad={(e) => URL.revokeObjectURL(e.target.src)}
                        />
                      </div>
                      <div
                        className="sample_file absolute_postion top-right-0 p-1 cursor-pointer"
                        onClick={() => {
                          setSelectedImage(null);
                          setFileName({ ...fileName, image: "" });
                          setImageData(null);
                        }}
                      >
                        <div className="cursor-pointer  tooltips-container">
                          <div className="bl-24px">
                            {fileName.image && <ImageCroseSvg />}
                          </div>
                          <span className="tooltip-text remove_tooltip">
                            Remove image
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {eventError.event_img && (
                  <div className="text-danger">{eventError.event_img}</div>
                )}
              </div>
            </div>

            <div className="mt-3">
              <div
                className={`event_file ${dragActive ? "drag-active" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  handleDrop(e);
                  handleFile(e.dataTransfer.files[0], "event_files");
                }}
              >
                <div className="upload-btn-wrapper">
                  <button
                    className="vitalu_cont "
                    onClick={() => pdfInputRef?.current.click()}
                    disabled={fileName.pdf}
                  >
                    <div className="upload_icon">
                      <UploadPdfSvg />
                    </div>
                    <div className="vital_text_png">
                      <span>Click to upload PDF File</span> or drag and drop{" "}
                      <br></br>( Please note: Only one PDF file can be uploaded per event, and the file size must not exceed 10 MB. )
                    </div>
                  </button>
                  <input
                    type="file"
                    accept=".pdf"
                    ref={pdfInputRef}
                    name="event_files"
                    onChange={(e) =>
                      handleFile(e.target.files[0], e.target.name)
                    }
                    style={{ display: "none" }}
                  />
                </div>

                {fileName.pdf && (
                  <>
                    <h2 className="inter">Uploaded PDF Files</h2>
                    <div className="data_pdf_container">
                      <button
                        className="sample_file_icon tooltip-container bgblack p-2 w-100"
                        onClick={() => setShowPdfPreview(!showPdfPreview)}
                      >
                        <span className="tooltips">
                          Click to Expand/Collapse PDF
                        </span>

                        <div className="sample_file cursor-pointer">
                          <p className="button_Drop">
                            {fileName.pdf !== null ? fileName.pdf : ""}
                          </p>
                        </div>
                        <div className="container_pdf_data">
                          <div className="ml-2 p-1 rounded hover:bg-gray-100 cursor-pointer">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="#fff"
                              stroke="black"
                              strokeWidth="1"
                              strokeLinecap="black"
                              strokeLinejoin="black"
                              className="lucide lucide-chevron-down"
                              style={{
                                transform: showPdfPreview
                                  ? "rotate(0deg)"
                                  : "rotate(180deg)",
                                transition: "transform 0.2s ease-in-out",
                              }}
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </div>
                        </div>
                      </button>
                    </div>
                    {!showPdfPreview && (
                      <div className="pdf-preview mt-3">
                        <div
                          className="sample_file mt-2 cursor-pointer aln-me-right tooltips-container"
                          onClick={() => {
                            setFileName({ ...fileName, pdf: "" });
                            setSelectedPdf(null);
                            setShowPdfPreview(false);
                          }}
                        >
                          {fileName.pdf && <ImageCroseSvg />}
                          <span className="tooltip-text">Remove PDF</span>
                        </div>

                        <PdfViewer
                          pdfUrl={
                            selectedPdf
                              ? URL.createObjectURL(selectedPdf)
                              : null
                          }
                          fileName={fileName.pdf}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn cancelbtn_event inter"
                onClick={() => onSetModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="blackbtn savebtn_event inter"
                onClick={(e) => handleSubmit(e)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewEvent;
